import classNames from 'classnames';
import React, { FormEvent, ReactNode, useCallback, useEffect, useState, useRef } from 'react';
import PortalToBody from 'react-portal-to-body';
import { defineWithDefaults } from 'rodash';
import { useBodyClasses } from '@rcm/react-utils';
import '../../styles/modal.scss';

interface Props {
  /**
   * Whether to put the modal in a shown state
   */
  show?: boolean;
  /**
   * Additional class names to put on the modal.
   */
  className?: string;
  /**
   * Additional class names to put on the modal's backdrop
   */
  backdropClassName?: string;
  /**
   * Additional class to put on the body while modal is open
   */
  bodyClassName?: string;
  /**
   * Action taken when the modal should close.
   */
  onClose?: () => void;
  /**
   * Content of the "modal-header" section.
   */
  head?: ReactNode;
  /**
   * Content of the "modal-footer" section.
   */
  foot?: ReactNode;
  /**
   * Content of the "modal-body" section.
   */
  children?: ReactNode;
  /**
   * Fires when the modal fade out has finished
   */
  onFadeOut?: () => void;
  /**
   * Whether to prevent rendering a backdrop
   */
  noBackdrop?: boolean;
}

const withDefaultProps = defineWithDefaults<Props>({
  backdropClassName: '',
  bodyClassName: '',
  className: '',
  foot: null,
  head: null,
  noBackdrop: false,
  show: true,
});

const Modal = (props: Props) => {
  props = withDefaultProps(props);

  const [stShow, setStShow] = useState(false);
  const [stFade, setStFade] = useState<'in' | 'out'>('out');
  const timeout = useRef(null);

  const handleClose = useCallback(
    (e: FormEvent<HTMLElement>) => {
      if (props.onClose && e.target === e.currentTarget) {
        props.onClose();
      }
    },
    [props.onClose]
  );

  const handleTransitionEnd = useCallback(() => {
    if (!props.show && stFade === 'out') {
      setStShow(false);
      if (props.onFadeOut) {
        props.onFadeOut();
      }
      return;
    }
    if (props.show && stFade === 'in') {
      setStShow(true);
    }
  }, [props.show, stFade]);

  useEffect(() => {
    if (props.show) {
      setStShow(true);
      window.setTimeout(() => setStFade('in'), 100);
    } else {
      setStFade('out');
    }
    return () => {
      if (timeout.current) {
        window.clearTimeout(timeout.current);
      }
    };
  }, [props.show]);

  useBodyClasses({
    [props.bodyClassName]: stShow && props.bodyClassName,
    'forms-modal-open': stShow,
    'modal-open': stShow,
  });

  return (
    <div
      className={classNames(props.className, 'modal', 'fade', stFade, { show: stShow })}
      tabIndex={-1}
      role="dialog"
      onClick={handleClose}
    >
      <div className="modal-dialog" onTransitionEnd={handleTransitionEnd} role="document">
        <div className="modal-content">
          {(props.head || props.onClose) && (
            <div className="modal-header">
              {props.onClose && (
                <button onClick={props.onClose} type="button" className="close" aria-label="Close">
                  <span aria-hidden="true">×</span>
                </button>
              )}
              {props.head}
            </div>
          )}
          {props.children && <div className="modal-body">{props.children}</div>}
          {props.foot && <div className="modal-footer">{props.foot}</div>}
        </div>
      </div>
      {stShow && !props.noBackdrop && (
        <PortalToBody>
          <div className={classNames('modal-backdrop', 'fade', stFade, props.backdropClassName)} />
        </PortalToBody>
      )}
    </div>
  );
};

export default Modal;
